/* eslint-disable react-refresh/only-export-components */
/**
 * Hand-tuned line icons for the Building Data widget.
 * Ported from `/tmp/design_bundle/building-data-sidebar-widget/project/src/icons.jsx`.
 *
 * Kept isolated from the app's lucide icon set so that the design's
 * 1.5-stroke / round-cap feel survives — the widget is visually a distinct
 * "tool surface" and should not be accidentally re-skinned by future lucide
 * upgrades.
 *
 * Exports a single `WidgetIcons` object keyed by icon name — a map-of-components
 * is idiomatic for icon bundles. The `react-refresh/only-export-components`
 * rule is disabled at the top of the file because the export is inherently a
 * namespace, not a component.
 */

import type { SVGProps } from "react";

interface IconProps extends Omit<SVGProps<SVGSVGElement>, "stroke"> {
  size?: number;
  stroke?: string;
  /** Stroke width. Design default is 1.5. */
  sw?: number;
  fill?: string;
}

function Icon({
  size = 16,
  stroke = "currentColor",
  sw = 1.5,
  fill = "none",
  children,
  ...rest
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const WidgetIcons = {
  building: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M5 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16" />
      <path d="M15 10h3a1 1 0 0 1 1 1v10" />
      <path d="M3 21h18" />
      <path d="M8 8h0M11 8h0M8 12h0M11 12h0M8 16h0M11 16h0" />
    </Icon>
  ),
  chevRight: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M9 6l6 6-6 6" />
    </Icon>
  ),
  sparkle: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.5 6.5l2.5 2.5M15 15l2.5 2.5M6.5 17.5l2.5-2.5M15 9l2.5-2.5" />
    </Icon>
  ),
  warning: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M12 3L2 20h20L12 3zM12 10v5M12 18h0" />
    </Icon>
  ),
  search: (p: IconProps = {}) => (
    <Icon {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4-4" />
    </Icon>
  ),
  source: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M4 5a2 2 0 0 1 2-2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5z" />
      <path d="M14 3v6h6" />
    </Icon>
  ),
  edit: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M4 20h4l10-10-4-4L4 16v4zM13.5 6.5l4 4" />
    </Icon>
  ),
  clock: (p: IconProps = {}) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Icon>
  ),
};
